import { useMemo } from "react";

import type { NewPlanDefault_EditPageQuery } from "@/lib/gql/__generated__/graphql";
import { SiteControllerType } from "@/lib/gql/__generated__/graphql";

import { useSendCustomMailSettingForCheckInBefore_00days } from "../../../../../../_shared/model/repository/mutation/check-in-before-00-days";
import { useSendCustomMailSettingForCheckInBefore_01days } from "../../../../../../_shared/model/repository/mutation/check-in-before-01-days";
import { useSendCustomMailSettingForCheckInBefore_07days } from "../../../../../../_shared/model/repository/mutation/check-in-before-07-days";
import { useSendCustomMailSettingForCheckOutAfter_01days } from "../../../../../../_shared/model/repository/mutation/check-out-after-01-days";
import { useSendCustomMailSettingForReservationCancel } from "../../../../../../_shared/model/repository/mutation/reservation-cancel";
import { useSendCustomMailSettingForReservationCreate } from "../../../../../../_shared/model/repository/mutation/reservation-create";
import { useSendCustomMailSettingForReservationUpdate } from "../../../../../../_shared/model/repository/mutation/reservation-update";
import { useInitProductPlanFormModelContext } from "../../../init";
import { useCreatePlan } from "../use-create";
import { serverModelToInputModelConverter } from "./converter/server-model-to-input-model/serverModelToInputModelConverter";

import type { ProductPlanFormModel } from "../../..";
import type { ProductNewPlanFormModelContextType } from "../../../../ui/new/context/context";

type Props = {
	serverModel: NewPlanDefault_EditPageQuery;
};

export const useInitialized = ({ serverModel }: Props): ProductNewPlanFormModelContextType => {
	const inputModel = useMemo(() => serverModelToInputModelConverter(serverModel), [serverModel]);

	const { hotel, hotelPolicy, customEmailSetting, roomList, optionList, discountList, siteControllerSetting } =
		serverModel;
	const queryData = useMemo(() => {
		return {
			hotelEmailAddress: hotel.email ?? "",
			hotelPolicy: {
				checkInStart: hotelPolicy?.checkInStart ?? "",
				checkInEnd: hotelPolicy?.checkInEnd ?? "",
				checkOut: hotelPolicy?.checkOut ?? "",
				requiredPrepMillisecond: hotelPolicy?.requiredPrepMillisecond ?? 0,
				questions: hotelPolicy?.questions ?? [],
			},
			customEmailSetting: {
				reservation: {
					atCreated: customEmailSetting?.reservation?.atCreated ?? "",
					atUpdated: customEmailSetting?.reservation?.atUpdated ?? "",
					atCanceled: customEmailSetting?.reservation?.atCanceled ?? "",
					atCin: customEmailSetting?.reservation?.atCin ?? "",
				},
			},
			connection: {
				roomNameAndIDOptions: [
					...roomList.rooms.map((room) => {
						return {
							label: room.name,
							id: `${room.roomID}_${room.name}`,
							value: room.roomID,
						};
					}),
					...roomList.dummy.map((room) => {
						return {
							label: room.name,
							id: `${room.roomID}_${room.name}`,
							value: room.roomID,
						};
					}),
				],
				optionNameAndIDOptions: optionList.options
					.filter((option) => option.inSale)
					.map((option) => {
						return {
							label: option.name,
							id: `${option.optionID}_${option.name}`,
							value: option.optionID,
						};
					}),
				discountNameAndIDOptions: [
					...discountList.ordinary
						.filter((discount) => discount.inSale)
						.map((discount) => {
							return {
								label: discount.name,
								id: `${discount.discountID}_${discount.name}`,
								value: discount.discountID,
							};
						}),
					...discountList.longTerm
						.filter((discount) => discount.inSale)
						.map((discount) => {
							return {
								label: discount.name,
								id: `${discount.discountID}_${discount.name}`,
								value: discount.discountID,
							};
						}),
					...discountList.multi
						.filter((discount) => discount.inSale)
						.map((discount) => {
							return {
								label: discount.name,
								id: `${discount.discountID}_${discount.name}`,
								value: discount.discountID,
							};
						}),
				],
			},
			siteControllerSetting: {
				siteControllerType: siteControllerSetting?.siteControllerType ?? SiteControllerType.NotUsing,
				setUpRoomPriceByChillnn: siteControllerSetting?.setUpRoomPriceByChillnn ?? false,
			},
			forReservation: {
				atCreated: {
					text: "",
					override: false,
				},
				atUpdated: {
					text: "",
					override: false,
				},
				atCanceled: {
					text: "",
					override: false,
				},
				atCheckInBefore_00days: {
					shouldSendEmail: false,
					text: "",
					override: false,
				},
				atCheckInBefore_01days: {
					shouldSendEmail: false,
					text: "",
					override: false,
				},
				atCheckInBefore_07days: {
					shouldSendEmail: false,
					text: "",
					override: false,
				},
				atCheckOutAfter_01days: {
					shouldSendEmail: false,
					text: "",
					override: false,
				},
			},
		} satisfies ProductPlanFormModel["QueryData"];
	}, [
		customEmailSetting?.reservation?.atCanceled,
		customEmailSetting?.reservation?.atCin,
		customEmailSetting?.reservation?.atCreated,
		customEmailSetting?.reservation?.atUpdated,
		discountList,
		hotel.email,
		hotelPolicy?.checkInEnd,
		hotelPolicy?.checkInStart,
		hotelPolicy?.checkOut,
		hotelPolicy?.questions,
		hotelPolicy?.requiredPrepMillisecond,
		optionList.options,
		roomList.dummy,
		roomList.rooms,
		siteControllerSetting?.setUpRoomPriceByChillnn,
		siteControllerSetting?.siteControllerType,
	]);

	const { onSetViewModel, viewModel, resetViewModel, initInputModel } = useInitProductPlanFormModelContext({ inputModel, queryData });

	const { onCreatePlan } = useCreatePlan({ viewModel, onReset: resetViewModel });

	const { sendTestMailAtReservationCreate } = useSendCustomMailSettingForReservationCreate();
	const { sendTestMailAtReservationUpdate } = useSendCustomMailSettingForReservationUpdate();
	const { sendTestMailAtReservationCancel } = useSendCustomMailSettingForReservationCancel();
	const { sendTestMailAtCheckInBefore07Days } = useSendCustomMailSettingForCheckInBefore_07days();
	const { sendTestMailAtCheckInBefore01Days } = useSendCustomMailSettingForCheckInBefore_01days();
	const { sendTestMailAtCheckInBefore00Days } = useSendCustomMailSettingForCheckInBefore_00days();
	const { sendTestMailAtCheckOutAfter01Days } = useSendCustomMailSettingForCheckOutAfter_01days();

	return {
		queryData,
		onSetViewModel,
		viewModel,
		initInputModel,
		resetViewModel,
		onUpdate: onCreatePlan,

		testEmailSender: {
			sendTestMailAtReservationCreate,
			sendTestMailAtReservationUpdate,
			sendTestMailAtReservationCancel,
			sendTestMailAtCheckInBefore07Days,
			sendTestMailAtCheckInBefore01Days,
			sendTestMailAtCheckInBefore00Days,
			sendTestMailAtCheckOutAfter01Days,
		},
	};
};
